import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { InfoPageLayout } from "@/components/pages/InfoPageLayout";
import { policyPageSchema, breadcrumbSchema, ldScripts } from "@/lib/seo/schema";

const TITLE = "Syarat & Ketentuan - Kalender Indonesia";
const DESC = "Syarat dan ketentuan penggunaan Kalender Indonesia, termasuk lisensi konten, batasan tanggung jawab, dan disclaimer data libur.";

export const Route = createFileRoute("/syarat-ketentuan")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: "Syarat & Ketentuan" },
      { property: "og:description", content: "Aturan penggunaan Kalender Indonesia." },
    ],
    links: [{ rel: "canonical", href: "https://hari.co.id/syarat-ketentuan/" }],
    scripts: ldScripts([
      policyPageSchema({ url: "/syarat-ketentuan", name: TITLE, description: DESC, kind: "TermsOfService", locale: "id", dateModified: "2026-05-01" }),
      breadcrumbSchema([
        { name: "Beranda", url: "/" },
        { name: "Syarat & Ketentuan", url: "/syarat-ketentuan" },
      ]),
    ]),
  }),
  component: TermsPage,
});

const TOC = [
  { id: "use", label: "Penggunaan situs" },
  { id: "accuracy", label: "Akurasi data" },
  { id: "license", label: "Hak cipta & lisensi" },
  { id: "liability", label: "Batasan tanggung jawab" },
  { id: "external", label: "Tautan eksternal" },
  { id: "changes", label: "Perubahan" },
];

function TermsPage() {
  return (
    <SiteShell>
      <InfoPageLayout
        variant="legal"
        title="Syarat & Ketentuan"
        subtitle="Aturan penggunaan situs dan disclaimer data."
        badge="T"
        lastUpdated="1 Mei 2026"
        locale="id"
        breadcrumbs={[{ label: "Beranda", href: "/" }, { label: "Syarat & Ketentuan" }]}
        aside={
          <div className="rounded-xl border border-border bg-card p-4 text-sm">
            <h3 className="mb-2 font-semibold">Daftar isi</h3>
            <ol className="space-y-1.5 text-muted-foreground">
              {TOC.map((t, i) => (
                <li key={t.id}>
                  <a className="hover:text-primary" href={`#${t.id}`}>{i + 1}. {t.label}</a>
                </li>
              ))}
            </ol>
          </div>
        }
      >
        <p>
          Dengan mengakses dan menggunakan situs <strong>Kalender Indonesia</strong>,
          Anda dianggap menyetujui syarat dan ketentuan berikut.
        </p>

        <h2 id="use">1. Penggunaan situs</h2>
        <ul>
          <li>Situs ini disediakan untuk tujuan referensi penanggalan secara umum.</li>
          <li>Anda dilarang menggunakan situs untuk aktivitas melanggar hukum, scraping massal yang membebani server, atau penyalahgunaan lain.</li>
        </ul>

        <h2 id="accuracy">2. Akurasi data</h2>
        <p>
          Data libur nasional dan cuti bersama bersumber dari SKB 3 Menteri.
          Konversi Hijriah, Jawa, dan Bali menggunakan metode aritmatika yang
          dapat berbeda 1 hari dengan rukyat resmi. Untuk keperluan resmi
          (administrasi, ibadah, dokumen hukum) silakan verifikasi ulang ke
          sumber resmi.
        </p>

        <h2 id="license">3. Hak cipta &amp; lisensi</h2>
        <p>
          Konten teks, desain, dan tata letak di situs ini adalah milik
          Kalender Indonesia. Data tanggal kalender bersifat publik. Anda boleh
          menyalin tanggal libur untuk penggunaan pribadi atau internal
          organisasi, tetapi <strong>tidak diperbolehkan</strong> menyalin
          seluruh halaman ke situs lain tanpa atribusi.
        </p>

        <h2 id="liability">4. Batasan tanggung jawab</h2>
        <p>
          Kami tidak bertanggung jawab atas kerugian yang timbul akibat
          penggunaan data dari situs ini, termasuk namun tidak terbatas pada
          kesalahan jadwal, perencanaan perjalanan, atau keputusan bisnis.
        </p>

        <h2 id="external">5. Tautan eksternal</h2>
        <p>
          Situs dapat memuat tautan ke pihak ketiga. Kami tidak bertanggung
          jawab atas konten atau kebijakan privasi pihak ketiga tersebut.
        </p>

        <h2 id="changes">6. Perubahan</h2>
        <p>
          Syarat dapat diperbarui kapan saja. Lihat juga{" "}
          <Link to="/kebijakan-privasi/">Kebijakan Privasi</Link> dan{" "}
          <Link to="/kontak/">Kontak</Link>.
        </p>
      </InfoPageLayout>
    </SiteShell>
  );
}